import { useEffect, useState } from 'react';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { addDaysYmd, israelToday } from '../utils/schedule';

// meta/syncRuns holds one record per sync job. lastOkAt is a Firestore Timestamp, a Date or
// an ISO string depending on the writer; a step skipped by the Knesset check has
// skipped: 'knesset-changed'.
interface SyncRunRecord {
  lastOkAt?: Timestamp | Date | string | null;
  steps?: Record<string, { skipped?: string } | undefined>;
}

const toDate = (v: Timestamp | Date | string): Date | null => {
  const d = v instanceof Timestamp ? v.toDate() : v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

/* One read per page load, shared by the hooks below. Resolved values are also kept
   synchronously, so the desktop shell (which remounts with every page) shows them on its
   first render after that. */
let runsPromise: Promise<Record<string, SyncRunRecord | undefined>> | null = null;
let lastSyncValue: Date | null = null;

const loadRuns = (): Promise<Record<string, SyncRunRecord | undefined>> => {
  if (!runsPromise) {
    runsPromise = getDoc(doc(db, 'meta', 'syncRuns'))
      .then(snap => (snap.exists() ? snap.data() : {}) as Record<string, SyncRunRecord | undefined>)
      .catch(() => { runsPromise = null; return {}; });
  }
  return runsPromise;
};

const newestOk = (runs: Record<string, SyncRunRecord | undefined>): Date | null => {
  let newest: Date | null = null;
  for (const run of Object.values(runs)) {
    const d = run && run.lastOkAt ? toDate(run.lastOkAt) : null;
    if (d && (!newest || d > newest)) newest = d;
  }
  return newest;
};

// Time of the most recent successful sync of any job, or null while loading / on failure
export const useLastSync = (): Date | null => {
  const [value, setValue] = useState<Date | null>(lastSyncValue);
  useEffect(() => {
    let alive = true;
    loadRuns().then(runs => { lastSyncValue = newestOk(runs); if (alive) setValue(lastSyncValue); });
    return () => { alive = false; };
  }, []);
  return value;
};

// When a sync job's steps are paused because a newer Knesset has been sworn in and the site
// hasn't moved to it yet: the time of the job's last successful run (what its data is
// current to). Null when it isn't paused, while loading, or on failure.
export const useSyncPause = (job: string): Date | null => {
  const [value, setValue] = useState<Date | null>(null);
  useEffect(() => {
    let alive = true;
    loadRuns().then(runs => {
      const run = runs[job];
      const steps = run && run.steps ? Object.values(run.steps) : [];
      const paused = steps.some(s => !!s && s.skipped === 'knesset-changed');
      if (alive) setValue(paused && run && run.lastOkAt ? toDate(run.lastOkAt) : null);
    });
    return () => { alive = false; };
  }, [job]);
  return value;
};

// 'עודכן היום, 09:00' / 'עודכן אתמול, 21:30' / 'עודכן ב־07/10', in Israel time
export const formatLastSync = (d: Date): string => {
  const day = d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Jerusalem' });
  const time = d.toLocaleTimeString('en-GB', { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit', hour12: false });
  const today = israelToday();
  if (day === today) return `עודכן היום, ${time}`;
  if (day === addDaysYmd(today, -1)) return `עודכן אתמול, ${time}`;
  return `עודכן ב־${day.slice(8, 10)}/${day.slice(5, 7)}`;
};

// '9 בנובמבר', in Israel time
export const formatDayMonth = (d: Date): string =>
  d.toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem', day: 'numeric', month: 'long' });
